import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper } from '@mui/material';
import { RideSchema } from '../../types/simulation';

interface TripDemandChartProps {
  rides: RideSchema[];
}

export const TripDemandChart: React.FC<TripDemandChartProps> = ({ rides }) => {
  const { t } = useTranslation('simulation');
  // Simple chart placeholder - can be enhanced later
  const hourlyDemand = rides.reduce((acc, ride) => {
    const hour = new Date(ride.request_time || ride.created_at || new Date()).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  const maxDemand = Math.max(...Object.values(hourlyDemand));
  
  // Calculate Y-axis scale
  const yAxisSteps = 5;
  const yAxisMax = Math.ceil(maxDemand / yAxisSteps) * yAxisSteps || yAxisSteps;
  const yAxisStep = yAxisMax / yAxisSteps;
  
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('setup.tripDemandChart.title')}
      </Typography>
      <Box sx={{ display: 'flex', height: 400 }}>
        {/* Y-axis */}
        <Box sx={{ 
          width: 40, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          pr: 1,
          py: 2
        }}>
          {Array.from({ length: yAxisSteps + 1 }, (_, i) => (
            <Typography 
              key={i} 
              variant="caption" 
              sx={{ 
                fontSize: '10px', 
                color: 'text.secondary',
                lineHeight: 1
              }}
            >
              {Math.round(yAxisMax - (i * yAxisStep))}
            </Typography>
          ))}
        </Box>
        
        {/* Chart area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'end', 
          gap: 1, 
          height: '100%',
          borderLeft: '1px solid #e0e0e0',
          borderBottom: '1px solid #e0e0e0',
          pl: 1,
          pb: 2
        }}>
          {Array.from({ length: 24 }, (_, hour) => (
            <Box key={hour} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, height: '100%' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-end', 
                height: 'calc(100% - 20px)',
                width: '100%'
              }}>
                <Box
                  sx={{
                    backgroundColor: 'primary.main',
                    width: '100%',
                    height: `${(hourlyDemand[hour] || 0) / yAxisMax * 100}%`,
                    minHeight: hourlyDemand[hour] ? '2px' : 0,
                    borderRadius: '2px 2px 0 0'
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ fontSize: '10px', mt: 0.5 }}>
                {hour}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('setup.tripDemandChart.totalTrips', { count: rides.length, peak: Object.keys(hourlyDemand).reduce((a, b) => hourlyDemand[parseInt(a)] > hourlyDemand[parseInt(b)] ? a : b, '0') })}
      </Typography>
    </Paper>
  );
};
